<template>
  <div class="block mx-auto">
    <div class="bg-grey-background-subdued sm:bg-white sm:box-shadow-sm p-16">
      <p class="text-overline text-grey-header">
        {{ isAdditionalProfile ? 'Profile added!' : "You're in!" }}
      </p>
      <div class="flex gap-16 mt-16">
        <ProgressBar :value="1" :max="1" :content="isAdditionalProfile ? 'Profile added!' : 'You\'re in!'" />
        <ProgressBar :value="1" :max="1" content="" />
      </div>
    </div>
    <div class="mx-auto lg:max-w-[986px]">
      <h1 class="text-h1 mt-24 mb-24 lg:mb-48 px-16">
        {{ isAdditionalProfile ? `You're ready to find new opportunities` : "You're ready to start applying to jobs" }}
      </h1>
      <div class="flex flex-col-reverse gap-[24px] lg:flex-row lg:gap-[48px]">
        <ProfileCompleteActions
          :is-additional-profile="isAdditionalProfile"
          :has-photo="hasPhoto"
          :formatted-care-type="formattedCareType"
          :job-search-link="jobSearchLink"
          :profile-edit-link="profileEditLink"
          :should-use-spa-routes="shouldUseSpaRoutes"
        />
        <ProfileCompletePreview :account="account" :profile-preview="profilePreview" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';
import ProgressBar from '@/components/progress_bar/ProgressBar.vue';
import ProfileCompleteActions from './ProfileCompleteActions.vue';
import ProfileCompletePreview from './ProfileCompletePreview.vue';
import { sendSegmentEvent } from '@/lib/segment';
import { readDomainCookie, deleteDomainCookie } from '@/lib/domain_cookie';
import { fetchProfileCompletePreview, ProfileCompletePreviewData } from '@/lib/sitterOnboarding/profileCompletePreview';
import { usesSpaRoutes } from '@/apps/provider/profile/helpers/profileEditUrlHelper';
export default {
  name: 'ProfileComplete',
  components: {
    ProgressBar,
    ProfileCompleteActions,
    ProfileCompletePreview,
  },
  data(): { profilePreview: ProfileCompletePreviewData | null } {
    return {
      profilePreview: null,
    };
  },
  computed: {
    account() {
      return this.$store.getters['account/getAccount'];
    },
    isAdditionalProfile(): boolean {
      // Prefer the URL signal (set by the legacy/SPA redirect) over the cookie so this terminal
      // page no longer depends on a cookie surviving the round-trip. Falls back to the cookie for
      // redirects that haven't been migrated to pass the query param yet.
      return this.queryParam('additionalProfile') === 'true' || readDomainCookie('additional_profile') === 'true';
    },
    careType(): string {
      return this.resolveCareType() || 'babysitter';
    },
    sanitizedCareType(): string {
      return snakeCase(this.careType) || 'babysitter';
    },
    formattedCareType(): string {
      return this.sanitizedCareType.split('_').join(' ');
    },
    // TODO - clean up this logic after all care types have been migrated to the new edit flow.
    shouldUseSpaRoutes(): boolean {
      return usesSpaRoutes(this.sanitizedCareType);
    },
    profileEditLink(): String {
      return this.shouldUseSpaRoutes
        ? `/provider/profile/${this.sanitizedCareType}/${this.account?.id}`
        : `/profile/provider/${this.sanitizedCareType}/edit_entry`;
    },
    jobSearchLink(): String {
      return `/provider/jobs/search?careType=${this.sanitizedCareType}&zipcode=${this.zipCode}`;
    },
    hasPhoto(): Boolean {
      return !!this.account?.croppedPhoto;
    },
    zipCode(): String {
      return this.account?.location?.zipcode || '';
    },
  },
  async created() {
    await this.loadProfilePreview();
  },
  mounted() {
    this.blurAllCards();

    if (this.queryParam('profileCompleted') === 'true' || readDomainCookie('profile_completed')) {
      this.trackProfileCompleted();
    }
  },
  methods: {
    queryParam(key: string): string | null {
      const value = this.$route?.query?.[key];
      if (value === undefined || value === null) return null;
      return Array.isArray(value) ? value[0] : value;
    },
    resolveCareType(): string {
      // Server/URL first: the care type comes from the redirect query param, then the live account
      // (providerType), and only falls back to the persisted Vuex draft. This keeps the page correct
      // after a logout/login or a fresh browser window where the sessionStorage draft is gone. The
      // store is checked before the account because, for additional profiles, the account's
      // providerType is the primary profile, not the one just completed.
      const fromQuery = this.queryParam('careType');
      if (fromQuery) return camelCase(fromQuery);

      const fromStore = this.$store.getters.careType;
      if (fromStore) return fromStore;

      if (this.account?.careType) return camelCase(this.account.careType);

      return '';
    },
    async loadProfilePreview() {
      const careType = this.resolveCareType();
      const accountId = this.account?.id;

      if (!accountId || !careType) return;

      try {
        this.profilePreview = await fetchProfileCompletePreview(this.$apollo, accountId, careType);
      } catch (error) {
        console.error('Failed to fetch profile preview on profile complete:', error);
      }
    },
    trackProfileCompleted() {
      deleteDomainCookie('profile_completed');

      if (this.isAdditionalProfile) {
        deleteDomainCookie('additional_profile');
      }

      sendSegmentEvent(this.isAdditionalProfile ? 'Sitter Additional Profile Completed' : 'Sitter Profile Completed', {
        profile_type: this.careType,
      });
    },
    blurAllCards() {
      this.$nextTick(() => {
        if (document.activeElement) {
          (document.activeElement as HTMLElement)?.blur();
        }
      });
    },
  },
};
</script>
